/**
 * Client-side range/required checks for the Telegram and MAX "Delivery & limits" numeric fields.
 *
 * The two channels' adapters enforce identical ranges (`TelegramServiceSettingsServiceImpl` /
 * `MaxServiceSettingsServiceImpl`: `deliveryMaxRetries` 0..10, `sendTimeoutMs` 100..120000,
 * `deliveryRetryBackoffMs`/`rateLimitPerChatPerMin` >= 0) and 422 `INVALID_RANGE` on a violation.
 * Before this fix, `TelegramSettingsPage`/`MaxSettingsPage` bound only a `min` on some fields and
 * coerced a cleared field to `0`/`1` (`value ?? 0`), so the portal could submit — and did submit,
 * per the stage defect this fixes — a value the server was guaranteed to reject. Shared here so
 * the two pages' bounds can't silently drift apart again the way the fields themselves once did
 * (see the `react-mui-developer` memory on the channel-independence work).
 *
 * Mirrors `features/chatbot-settings/model/validation.ts`'s identical `outOfRange` (a `null`
 * value is never "out of range" on its own — that is a required-ness concern, handled separately
 * by {@link deliveryFieldError} below).
 */

/** Loosely-typed to match react-i18next's `t` without dragging in the full `TFunction` generics
 * (mirrors `features/catalog/lib/attributeFilterDisplay.ts`). */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

export function outOfRange(value: number | null, min: number, max: number): boolean {
  return value !== null && (value < min || value > max);
}

/**
 * Inline error text for a Delivery & limits field, or `null` when valid.
 *
 * - An empty field (`value === null`) is required — never coerce it to a default; keep it empty
 *   and invalid until the merchant enters a value.
 * - Omit `max` for an open-ended ("at least `min`") range, e.g. retry backoff or the rate limit.
 *
 * `keyPrefix` selects the owning page's i18n section — both read from the `settings` namespace,
 * differing only in `telegramSettings.deliverySection.*` vs `maxSettings.deliverySection.*`.
 */
export function deliveryFieldError(
  t: Translate,
  keyPrefix: 'telegramSettings.deliverySection' | 'maxSettings.deliverySection',
  value: number | null,
  min: number,
  max?: number,
): string | null {
  if (value === null) {
    return t(`${keyPrefix}.requiredError`);
  }
  if (max !== undefined) {
    return outOfRange(value, min, max) ? t(`${keyPrefix}.rangeError`, { min, max }) : null;
  }
  return value < min ? t(`${keyPrefix}.minError`, { min }) : null;
}
